import React from 'react';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { TablePaginationControls } from '~/components/TablePaginationControls/TablePaginationControls.tsx';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import { guaranteesClientText } from '~/modules/guarantees/form/client/constants.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import type { TGuaranteesAccountOption } from '../../../rootSection/types.ts';
import { areSameAccountOptions } from '../selectionUtils.ts';
import AccountTableRow from './AccountTableRow.tsx';

const ACCOUNT_TABLE_DEFAULT_ROWS_PER_PAGE = 10;

interface IAccountTableProps {
  accountOptions: TGuaranteesAccountOption[];
  isDisabled: boolean;
  isLoading: boolean;
  selectedAccount: TGuaranteesAccountOption | null;
  onAccountSelect: (option: TGuaranteesAccountOption) => void;
}

const AccountTable: React.FC<IAccountTableProps> = ({
  accountOptions,
  isDisabled,
  isLoading,
  selectedAccount,
  onAccountSelect,
}) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(ACCOUNT_TABLE_DEFAULT_ROWS_PER_PAGE);
  const totalElements = accountOptions.length;
  const paginatedAccountOptions = React.useMemo(
    () => accountOptions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [accountOptions, page, rowsPerPage]
  );

  React.useEffect(() => {
    setPage(0);
  }, [accountOptions]);

  React.useEffect(() => {
    const totalPages = Math.ceil(totalElements / rowsPerPage);

    if (page > 0 && page >= totalPages) {
      setPage(Math.max(totalPages - 1, 0));
    }
  }, [page, rowsPerPage, totalElements]);

  const handleRowsPerPageChange = (nextRowsPerPage: number) => {
    setRowsPerPage(nextRowsPerPage);
    setPage(0);
  };

  return (
    <>
      <TableContainer sx={dataFormStyles.documentTableContainer}>
        <Table
          sx={
            [
              dataFormStyles.documentTable,
              guaranteesClientPageStyles.flowAccountTable,
            ] as SxProps<Theme>
          }
        >
          <TableHead sx={dataFormStyles.documentTableHead}>
            <TableRow>
              <TableCell sx={(theme) => dataFormStyles.documentTableHeadCell(theme, 'name')}>
                {guaranteesClientText.accountIbanLabel}
              </TableCell>
              <TableCell
                align="center"
                sx={(theme) => dataFormStyles.documentTableHeadCell(theme, 'status')}
              >
                {guaranteesClientText.accountFrozenLabel}
              </TableCell>
              <TableCell
                align="center"
                sx={(theme) => dataFormStyles.documentTableHeadCell(theme, 'actions')}
              >
                {guaranteesClientText.actionsLabel}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={dataFormStyles.documentEmptyCell}>
                  <Box sx={guaranteesClientPageStyles.flowLoadingCell}>
                    <CircularProgress size={24} />
                    {guaranteesClientText.accountLoadingText}
                  </Box>
                </TableCell>
              </TableRow>
            ) : !accountOptions.length ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={dataFormStyles.documentEmptyCell}>
                  {guaranteesClientText.accountNoOptionsText}
                </TableCell>
              </TableRow>
            ) : (
              paginatedAccountOptions.map((option) => (
                <AccountTableRow
                  key={option.value}
                  isDisabled={isDisabled}
                  isSelected={!!selectedAccount && areSameAccountOptions(option, selectedAccount)}
                  option={option}
                  onAccountSelect={onAccountSelect}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePaginationControls
        page={page}
        rowsPerPage={rowsPerPage}
        totalElements={totalElements}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </>
  );
};

export default AccountTable;
